import React, { Fragment, useState, useEffect, useMemo } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import PortalMenu from "../Common/PortalMenu";
import {
 Row,
 Col,
 Card,
 Table,
 Badge,
 Dropdown,
 Button,
 Spinner,
} from "react-bootstrap";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import AppPagination from "../Common/AppPagination";
import PageTitle from "../../layouts/PageTitle";
import api, { getFileUrl } from "../../../api";

const DebitList = () => {
 const navigate = useNavigate();
 const [expenses, setExpenses] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [projects, setProjects] = useState([]);
 const [expenseTypes, setExpenseTypes] = useState([]);
 const [loading, setLoading] = useState(false);
 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0,
 limit: 10,
 });
 const [searchParams] = useSearchParams();

 const workOrderFromUrl = searchParams.get("workOrder") || "";
 const projectFromUrl = searchParams.get("project") || "";
 const expenseTypeFromUrl = searchParams.get("expenseType") || "";
 const fromDateFromUrl = searchParams.get("fromDate") || "";
 const toDateFromUrl = searchParams.get("toDate") || "";

 const [filters, setFilters] = usePersistentFilters("debit_list_filters", {
 workOrder: workOrderFromUrl,
 project: projectFromUrl,
 expenseType: expenseTypeFromUrl,
 fromDate: fromDateFromUrl,
 toDate: toDateFromUrl,
 page: 1,
 limit: 10,
 });

 useEffect(() => {
 setFilters((prev) => ({
 ...prev,
 workOrder: workOrderFromUrl,
 project: projectFromUrl,
 expenseType: expenseTypeFromUrl || prev.expenseType,
 fromDate: fromDateFromUrl || prev.fromDate,
 toDate: toDateFromUrl || prev.toDate,
 }));
 }, [workOrderFromUrl, projectFromUrl, expenseTypeFromUrl, fromDateFromUrl, toDateFromUrl]);

 useEffect(() => {
 fetchWorkOrders();
 fetchProjects();
 fetchExpenseTypes();
 fetchExpenses();
 }, [filters, workOrderFromUrl, projectFromUrl]);

 const fetchProjects = async () => {
 try {
 const res = await api.get("/projects?limit=1000");
 if (res.data?.success) setProjects(res.data.data || []);
 } catch (err) {
 console.error("Error fetching projects:", err);
 }
 };

 const availableWorkOrders = useMemo(() => {
 if (!filters.project) return workOrders;
 return workOrders.filter((wo) => {
 const pId = wo.project?._id || (typeof wo.project === "string" ? wo.project : null);
 return String(pId) === String(filters.project);
 });
 }, [workOrders, filters.project]);

 const fetchWorkOrders = async () => {
 try {
 const res = await api.get("/work-orders");
 if (res.data.success) setWorkOrders(res.data.data);
 } catch (err) {
 console.error("Error fetching work orders:", err);
 }
 };

 const fetchExpenseTypes = async () => {
 try {
 const res = await api.get("/expense-types?status=Active");
 if (res.data.success) setExpenseTypes(res.data.data);
 } catch (err) {
 console.error("Error fetching expense types:", err);
 }
 };

 const fetchExpenses = async () => {
 try {
 setLoading(true);
 const params = new URLSearchParams();
 if (filters.workOrder) params.append("workOrder", filters.workOrder);
 if (filters.project) params.append("project", filters.project);
 if (filters.expenseType) params.append("expenseType", filters.expenseType);
 if (filters.fromDate) params.append("fromDate", filters.fromDate);
 if (filters.toDate) params.append("toDate", filters.toDate);
 params.append("page", filters.page);
 params.append("limit", filters.limit);

 const res = await api.get(`/expenses?${params.toString()}`);
 if (res.data.success) {
 setExpenses(res.data.data || []);
 setPagination((prev) => ({
          ...prev,
          ...res.data.pagination,
          limit: Number(res.data.pagination?.limit || filters.limit || prev.limit || 10),
        }));
 }
 } catch (err) {
 console.error("Error fetching expenses:", err);
 } finally {
 setLoading(false);
 }
 };

 const [downloading, setDownloading] = useState(false);

 const handleDownloadPdf = async () => {
 try {
 setDownloading(true);
 const params = new URLSearchParams();
 if (filters.workOrder) params.append("workOrder", filters.workOrder);
 if (filters.project) params.append("project", filters.project);
 if (filters.expenseType) params.append("expenseType", filters.expenseType);
 if (filters.fromDate) params.append("fromDate", filters.fromDate);
 if (filters.toDate) params.append("toDate", filters.toDate);

 const token = localStorage.getItem("token"); 
 const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
 // Ensure we don't have double slashes if API_URL ends with /
 const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
 const url = `${cleanBaseUrl}/api/expenses/download-pdf?${params.toString()}`;

 const response = await fetch(url, {
 method: 'GET',
 headers: {
 'Authorization': `Bearer ${token}`,
 'Accept': 'application/pdf'
 }
 });

 if (!response.ok) {
 const errorText = await response.text();
 let message = "Failed to generate PDF";
 try {
 const errJson = JSON.parse(errorText);
 message = errJson.message || message;
 } catch (e) {}
 throw new Error(message);
 }

 const blob = await response.blob();
 if (blob.size === 0) throw new Error("Received empty PDF file");

 const downloadUrl = window.URL.createObjectURL(blob);
 const link = document.createElement('a');
 link.href = downloadUrl;
 link.setAttribute('download', `Expense_Report_${new Date().getTime()}.pdf`);
 document.body.appendChild(link);
 link.click();
 link.remove();
 window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 3000);
 } catch (err) {
 console.error("Error downloading PDF:", err);
 Swal.fire("Error", err.message || "Failed to download PDF. Please ensure the server is running correctly.", "error");
 } finally {
 setDownloading(false);
 }
 };

 const handleDelete = async (id) => {
 const result = await Swal.fire({
 title: "Are you sure?",
 text: "You won't be able to revert this!",
 icon: "warning",
 showCancelButton: true,
 confirmButtonColor: "#3085d6",
 cancelButtonColor: "#d33",
 confirmButtonText: "Yes, delete it!",
 });

 if (result.isConfirmed) {
 try {
 const res = await api.delete(`/expenses/${id}`);
 if (res.data.success) {
 Swal.fire("Deleted!", "Expense has been deleted.", "success");
 fetchExpenses();
 }
 } catch (err) {
 Swal.fire("Error", "Failed to delete expense", "error");
 }
 }
 };

 const formatDate = (date) => {
 if (!date) return "—";
 return new Date(date).toLocaleDateString("en-IN", {
 day: "2-digit",
 month: "short",
 year: "numeric",
 });
 };

 const formatCurrency = (amount) =>
 `₹ ${parseFloat(amount || 0).toLocaleString("en-IN", {
 minimumFractionDigits: 2,
 })}`;

 const getExpenseBills = (item) => {
 const bills = [
 ...(item.bill ? [item.bill] : []),
 ...(Array.isArray(item.bills) ? item.bills : []),
 ].filter(Boolean);

 return bills.filter((bill, index, self) => {
 const key = `${bill.filePath || ""}::${bill.fileName || ""}`;
 return index === self.findIndex((entry) => `${entry.filePath || ""}::${entry.fileName || ""}` === key);
 });
 };

 const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24">
 <circle fill="#000000" cx="5" cy="12" r="2"></circle>
 <circle fill="#000000" cx="12" cy="12" r="2"></circle>
 <circle fill="#000000" cx="19" cy="12" r="2"></circle>
 </svg>
 );

 return (
 <Fragment>
 <PageTitle activeMenu="Debit List" motherMenu="Accounts" />

 {/* Filter Bar */}
 <Row className="mb-3">
 <Col lg={12}>
 <Card style={{ minHeight: '160px' }}>
 <Card.Body>
 <Row className="g-3">
 <Col md={3}>
 <label className="form-label">Work Order</label>
 <select
 className="form-select form-select-sm"
 value={filters.workOrder || ""}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 workOrder: e.target.value,
 page: 1,
 }))
 }
 >
 <option value="">All Work Orders</option>
 {availableWorkOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo || wo.woNumber}{wo.label ? ` – ${wo.label}` : ""}
 </option>
 ))}
 </select>
 </Col>
 <Col md={3}>
 <label className="form-label">Expense Type</label>
 <select
 className="form-select form-select-sm"
 value={filters.expenseType || ""}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 expenseType: e.target.value,
 page: 1,
 }))
 }
 >
 <option value="">All Types</option>
 {expenseTypes.map((type) => (
 <option key={type._id} value={type.name}>
 {type.name}
 </option>
 ))}
 </select>
 </Col>
 <Col md={2}>
 <label className="form-label">From Date</label>
 <input
 type="date"
 className="form-control form-control-sm"
 value={filters.fromDate || ""}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 fromDate: e.target.value,
 page: 1,
 }))
 }
 />
 </Col>
 <Col md={2}>
 <label className="form-label">To Date</label>
 <input
 type="date"
 className="form-control form-control-sm"
 value={filters.toDate || ""}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 toDate: e.target.value,
 page: 1,
 }))
 }
 />
 </Col>
 <Col md={2}>
 <label className="form-label">Per Page</label>
 <select
 className="form-select form-select-sm"
 value={filters.limit || 10}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 limit: Number(e.target.value),
 page: 1,
 }))
 }
 >
 <option value={10}>10</option>
 <option value={25}>25</option>
 <option value={50}>50</option>
 </select>
 </Col>
 <Col md={12} className="d-flex gap-2">
 <Button 
 variant="primary" 
 className="flex-grow-1"
 onClick={handleDownloadPdf}
 style={{ height: '45px' }}
 disabled={downloading}
 >
 {downloading ? (
 <>
 <Spinner
 as="span"
 animation="border"
 size="sm"
 role="status"
 aria-hidden="true"
 className="me-2"
 />
 Generating PDF...
 </>
 ) : (
 <>
 <i className="fa fa-file-pdf me-2"></i> Download PDF
 </>
 )}
 </Button>
 <Button 
 variant="outline-danger" 
 className="flex-grow-1"
 onClick={() => setFilters({
 workOrder: "",
 project: "",
 expenseType: "",
 fromDate: "",
 toDate: "",
 page: 1,
 limit: 10,
 })}
 style={{ height: '45px' }}
 >
 <i className="fa fa-undo me-2"></i> Reset Filters
 </Button>
 </Col>
 </Row>
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>Debit (Expenses)</Card.Title>
 <div className="d-flex gap-2">
 <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
 Back
 </Button>
 <Button
                  variant="primary"
                  onClick={() => {
                    const targetWo = filters.workOrder || workOrderFromUrl;
                    const targetExpenseType = filters.expenseType || expenseTypeFromUrl;
                    const params = new URLSearchParams();
                    if (targetWo) params.append("workOrder", targetWo);
                    if (targetExpenseType) params.append("expenseType", targetExpenseType);
                    const qStr = params.toString();
                    navigate(qStr ? `/debit/add?${qStr}` : "/debit/add");
                  }}
                >
                  <i className="fa fa-plus me-2"></i> Add Expense
                </Button>
 </div>
 </Card.Header>
 <Card.Body>
 <div style={{ overflow: 'visible' }}>
 <Table className="w-100">
 <thead>
 <tr>
 <th>Sr. No.</th>
 <th>Date</th>
 <th>Type</th>
 <th>Amount</th>
 <th>Total (incl. GST)</th>
 <th>Payment Mode</th>
 <th>Paid To</th>
 <th>Work Order</th>
 <th>Actions</th>
 </tr>
 </thead>
 <tbody>
 {loading ? (
 <tr>
 <td colSpan="9" className="text-center py-4">
 <Spinner animation="border" variant="primary" />
 </td>
 </tr>
 ) : expenses.length === 0 ? (
 <tr>
 <td colSpan="9" className="text-center py-4 text-muted">
 No expenses found.
 </td>
 </tr>
 ) : (
 expenses.map((item, index) => (
 <tr key={item._id}>
 <td>{((pagination.currentPage || 1) - 1) * (pagination.limit || 10) + index + 1}</td>
 <td>{formatDate(item.expenseDate)}</td>
 <td>
                        <Badge bg="info">{item.expenseType}</Badge>
                        {item.recurringFrequency ? (
                          <Badge bg="primary" className="ms-1" style={{ fontSize: "10px" }}>
                            <i className="fa fa-sync-alt me-1"></i>{item.recurringFrequency}
                          </Badge>
                        ) : null}
                      </td>
 <td>{formatCurrency(item.amount)}</td>
 <td>{formatCurrency(item.totalAmount)}</td>
 <td>{item.paymentMode}</td>
 <td>{item.paidTo || "—"}</td>
 <td>{item.workOrder?.woNo || item.workOrder?.woNumber || "—"}</td>
 <td style={{ overflow: 'visible' }}>
 <Dropdown className="ms-auto">
 <Dropdown.Toggle
 as="div"
 className="btn-link p-0 no-caret"
 style={{ cursor: "pointer" }}
 >
 {svgMenu}
 </Dropdown.Toggle>
 <Dropdown.Menu align="end" as={PortalMenu}>
 {getExpenseBills(item).length > 0 && (
 <>
 {getExpenseBills(item).map((bill, billIndex) => (
 <Dropdown.Item
 key={`${bill.filePath || bill.fileName || billIndex}`}
 href={getFileUrl(bill.filePath)}
 target="_blank"
 rel="noreferrer"
 >
 <i className="fa fa-file-pdf me-2 text-danger"></i>
 {bill.fileName ? `View Bill ${billIndex + 1}` : "View Bill"}
 </Dropdown.Item>
 ))}
 </>
 )}
 <Dropdown.Item as={Link} to={(filters.workOrder || workOrderFromUrl) ? `/debit/edit/${item._id}?workOrder=${filters.workOrder || workOrderFromUrl}` : `/debit/edit/${item._id}`}>
 <i className="fa fa-edit me-2 text-primary"></i> Edit
 </Dropdown.Item>
 <Dropdown.Item onClick={() => handleDelete(item._id)} className="text-danger">
 <i className="fa fa-trash me-2"></i> Delete
 </Dropdown.Item>
 </Dropdown.Menu>
 </Dropdown>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </Table>
 </div>
 <AppPagination
 currentPage={pagination.currentPage}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={expenses.length}
 limit={pagination.limit}
 onPageChange={(page) => setFilters(p => ({ ...p, page: page }))}
 />
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default DebitList;
